import { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import {
  AlertTriangle, AlertCircle, CheckCircle2, Clock, ListTodo, User, Zap,
  MessageSquare, FileText, Presentation, Calendar, BarChart2, ExternalLink, RefreshCw,
  Loader2, Check,
} from 'lucide-react';
import type { Project, Ticket, RiskCache, FunctionalRole, SessionConfig, SuggestedAction, ActionType, ActionCompletion } from '../../types';
import type { TicketStats } from '../../lib/api';
import { useUserStore } from '../../store/userStore';
import { useEffectiveRole } from '../../hooks/useEffectiveRole';
import { ActionModal } from '../actions/ActionModal';
import { useActionStore } from '../../store/actionStore';
import { track } from '../../lib/telemetry';
import { getRiskBand, getRiskBandDisplay, formatRiskScore, DEFAULT_ALERT_THRESHOLD } from '../../lib/riskBand';
import { riskTrendToDirection, invertDirection } from '../../lib/trend';
import { confidenceFromScore } from '../../lib/deliveryConfidence';
import { TrendIndicator } from '../TrendIndicator';
import { CollapsibleText } from '../CollapsibleText';
import { deriveActionTriggerState, completionByLine } from '../../lib/actionCompletion';

interface AdaptiveViewProps {
  project: Project;
  tickets: Ticket[];
  risk: RiskCache | undefined;
  ticketStats: TicketStats | undefined;
  sessionConfig: SessionConfig | null;
  /** Opens the shared TicketDetailPanel — the same path TicketCard rows and
   * the Detail view's grouped lists use. Every ticket-shaped row in this
   * view must go through this rather than growing a second "open ticket"
   * implementation. */
  onTicketSelect: (ticket: Ticket) => void;
}

// ── Action icons ──────────────────────────────────────────────────────────────

const ACTION_ICONS: Partial<Record<ActionType, React.ElementType>> = {
  slack_message:          MessageSquare,
  slack_dm:               MessageSquare,
  create_doc:             FileText,
  create_slides:          Presentation,
  create_calendar_event:  Calendar,
  post_ticket_comment:    MessageSquare,
  update_ticket_status:   RefreshCw,
  status_report:          BarChart2,
  open_url:               ExternalLink,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

// Derived from the shared band so this panel's colors agree with every
// other risk surface in the app rather than reinventing cut-points.
function scoreColor(score: number, alertThreshold: number) {
  const band = getRiskBand(score, alertThreshold);
  if (band === 'low') return { text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800/40' };
  if (band === 'medium') return { text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800/40' };
  if (band === 'high') return { text: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800/40' };
  // null band — 0 sentinel ("unscored") or out-of-range: neutral, not a false "Low"
  return { text: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-800/40', border: 'border-gray-200 dark:border-gray-700/40' };
}

// Delivery confidence always derives from overallScore via the shared
// lib/deliveryConfidence.ts so the number is consistent across views —
// see that module for the unscored (null/0) rule.

function confidenceColor(pct: number) {
  if (pct >= 70) return 'text-emerald-600 dark:text-emerald-400';
  if (pct >= 40) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

// ── Shared panel card wrapper ─────────────────────────────────────────────────

function Card({ title, icon: Icon, children, className = '' }: {
  title: string;
  icon?: React.ElementType;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-5 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="h-4 w-4 text-gray-400" />}
        <h3 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ── Panel: Project Pulse ──────────────────────────────────────────────────────

function ProjectPulse({
  risk,
  ticketStats,
  alertThreshold,
}: {
  risk?: RiskCache;
  ticketStats?: TicketStats;
  alertThreshold: number;
}) {
  if (!risk || risk.overallScore == null) return null;

  const colors = scoreColor(risk.overallScore, alertThreshold);
  const dc = confidenceFromScore(risk.overallScore);
  // One underlying movement, two metrics: the risk score's direction, and its
  // inverse for confidence (dc is a monotonic inversion of the score). Each
  // tile declares its own polarity; TrendIndicator derives the colour.
  const riskDirection = riskTrendToDirection(risk.trend);
  const confidenceDirection = invertDirection(riskDirection);
  const topRisks = [...(risk.facets ?? [])].sort((a, b) => b.score - a.score).slice(0, 2);
  const completePct = ticketStats && ticketStats.total > 0
    ? Math.round((ticketStats.complete / ticketStats.total) * 100)
    : 0;

  return (
    <Card title="Project Pulse" icon={Zap} className="lg:col-span-2">
      <div className="grid grid-cols-3 gap-4 mb-5">
        {/* Risk score */}
        <div className={`rounded-xl p-5 text-center ${colors.bg} ${colors.border} border`}>
          <div
            title={`Score: ${formatRiskScore(risk.overallScore)} / 5.0`}
            className={`text-2xl font-bold cursor-default ${colors.text}`}
          >
            {getRiskBandDisplay(risk.overallScore, alertThreshold).label}
          </div>
          <div className="text-meta text-gray-500 mt-1">Risk Score</div>
          <TrendIndicator direction={riskDirection} polarity="lower-is-better" className="flex mt-1.5" />
        </div>

        {/* Delivery confidence */}
        <div className="rounded-xl p-5 text-center bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
          <div className={`text-4xl font-bold tabular-nums tracking-tight ${dc !== null ? confidenceColor(dc) : 'text-gray-400 dark:text-gray-500'}`}>
            {dc !== null ? `${dc}%` : '—'}
          </div>
          <div className="text-meta text-gray-500 mt-1">Delivery Confidence</div>
          <TrendIndicator direction={confidenceDirection} polarity="higher-is-better" className="flex mt-1.5" />
        </div>

        {/* Ticket progress */}
        <div className="rounded-xl p-5 text-center bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
          <div className="text-4xl font-bold tabular-nums tracking-tight text-primary">{completePct}%</div>
          <div className="text-meta text-gray-500 mt-1">Tickets Done</div>
          <div className="mt-2 h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${completePct}%` }}
            />
          </div>
        </div>
      </div>

      {topRisks.length > 0 && (
        <div className="space-y-4">
          <p className="text-meta font-medium text-gray-400 uppercase tracking-wide mb-2">Top Risk Signals</p>
          {topRisks.map((f) => {
            const fc = scoreColor(f.score, alertThreshold);
            return (
              <div key={f.name} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5">
                <span
                  title={`Score: ${formatRiskScore(f.score)} / 5.0`}
                  className={`text-micro font-bold mt-0.5 cursor-default ${fc.text}`}
                >
                  {getRiskBandDisplay(f.score, alertThreshold).label}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-meta font-semibold text-gray-700 dark:text-gray-300">{f.name}</p>
                  {/* Keyed by facet name — stable across re-evaluations. */}
                  <CollapsibleText key={f.name} collapsedLines={2} className="mt-0.5">
                    <p className="text-meta text-gray-500 dark:text-gray-400">{f.reasoning}</p>
                  </CollapsibleText>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Panel: My Work ────────────────────────────────────────────────────────────

function MyWork({ tickets, userId, onTicketSelect }: { tickets: Ticket[]; userId: string; onTicketSelect: (t: Ticket) => void }) {
  const mine = tickets.filter(
    (t) => t.assigneeId === userId && t.status !== 'complete' && t.status !== 'invalid' && t.status !== 'idea'
  );

  const statusBadge: Record<string, string> = {
    triage: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
    at_risk: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    on_track: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    planning: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  };
  const statusLabel: Record<string, string> = {
    triage: 'Triage', at_risk: 'At Risk', on_track: 'On Track', planning: 'Planning',
  };

  return (
    <Card title="My Work" icon={User}>
      {mine.length === 0 ? (
        <div className="text-center py-6">
          <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
          <p className="text-meta text-gray-500">Nothing assigned to you</p>
        </div>
      ) : (
        <div className="space-y-4">
          {mine.slice(0, 6).map((t) => (
            <button
              key={t.ticketId}
              type="button"
              onClick={() => onTicketSelect(t)}
              className="w-full text-left flex items-start gap-2.5 p-2.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors"
            >
              <span className={`shrink-0 text-meta font-semibold px-1.5 py-0.5 rounded-md mt-0.5 ${statusBadge[t.status] ?? 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400'}`}>
                {statusLabel[t.status] ?? t.status}
              </span>
              <p className="text-title text-gray-700 dark:text-gray-300 line-clamp-2 leading-snug">{t.title}</p>
            </button>
          ))}
          {mine.length > 6 && (
            <p className="text-meta text-gray-400 text-center pt-1">+{mine.length - 6} more</p>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Panel: What's Next ────────────────────────────────────────────────────────

function WhatsNext({ tickets, onTicketSelect }: { tickets: Ticket[]; onTicketSelect: (t: Ticket) => void }) {
  const next = tickets
    .filter((t) => ['triage', 'at_risk', 'planning'].includes(t.status) && t.status !== 'invalid')
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
    .slice(0, 5);

  return (
    <Card title="What's Next" icon={ListTodo}>
      {next.length === 0 ? (
        <p className="text-meta text-gray-400 text-center py-4">No open items</p>
      ) : (
        <div className="space-y-4">
          {next.map((t) => (
            <button
              key={t.ticketId}
              type="button"
              onClick={() => onTicketSelect(t)}
              className="w-full text-left flex items-start gap-2.5 p-2.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="text-title text-gray-700 dark:text-gray-300 line-clamp-2 leading-snug">{t.title}</p>
                {t.assigneeName ? (
                  <p className="text-meta text-gray-400 mt-0.5 flex items-center gap-1">
                    <User className="h-4 w-4" />{t.assigneeName}
                  </p>
                ) : (
                  <p className="text-meta text-amber-500 mt-0.5">Unassigned</p>
                )}
              </div>
              {t.dueDate && (
                <span className="shrink-0 text-meta text-gray-400 flex items-center gap-1 mt-0.5">
                  <Clock className="h-4 w-4" />{new Date(t.dueDate).toLocaleDateString()}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Panel: Team Workload ──────────────────────────────────────────────────────

function TeamWorkload({ tickets }: { tickets: Ticket[] }) {
  const active = tickets.filter((t) => !['complete', 'invalid', 'idea'].includes(t.status));
  const byAssignee = active.reduce<Record<string, { name: string; count: number }>>((acc, t) => {
    const key = t.assigneeId ?? '__unassigned__';
    const name = t.assigneeName ?? 'Unassigned';
    if (!acc[key]) acc[key] = { name, count: 0 };
    acc[key].count++;
    return acc;
  }, {});

  const sorted = Object.values(byAssignee).sort((a, b) => b.count - a.count).slice(0, 6);
  const max = Math.max(...sorted.map((s) => s.count), 1);

  return (
    <Card title="Team Workload" icon={User}>
      {sorted.length === 0 ? (
        <p className="text-meta text-gray-400 text-center py-4">No active tickets</p>
      ) : (
        <div className="space-y-4">
          {sorted.map((s) => (
            <div key={s.name}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-body text-gray-700 dark:text-gray-300 truncate">{s.name}</span>
                <span className="text-meta tabular-nums text-gray-500 ml-2 shrink-0">{s.count}</span>
              </div>
              <div className="h-1.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary/60 rounded-full transition-all"
                  style={{ width: `${(s.count / max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Panel: Ticket Stats ───────────────────────────────────────────────────────

function TicketBreakdown({ tickets }: { tickets: Ticket[] }) {
  const now = Date.now();
  // 5 days with no touch — matches backend default staleTicketDays (any update resets the clock)
  const staleMs = 5 * 24 * 60 * 60 * 1000;

  const counts = tickets.reduce(
    (acc, t) => {
      const DONE = new Set(['complete', 'done', 'closed', 'cancelled', 'wont_fix']);
      if (DONE.has(t.status)) acc.complete++;
      else if (t.status === 'invalid' || t.status === 'idea') acc.other++;
      else {
        acc.active++;
        // Use the most recent of external source update or our own Firestore updatedAt.
        // This ensures tickets updated inside OctoPM (status changes, new tickets) are
        // never counted stale just because the external source hasn't synced yet.
        const sourceTs = t.sourceUpdatedAt ? new Date(t.sourceUpdatedAt).getTime() : 0;
        const localTs  = t.updatedAt        ? new Date(t.updatedAt).getTime()        : 0;
        const updated  = Math.max(sourceTs, localTs);
        if (updated > 0 && now - updated > staleMs) acc.stale++;
      }
      if (t.dueDate) {
        const due = new Date(t.dueDate).getTime();
        if (due > now && due - now < 7 * 24 * 60 * 60 * 1000) acc.dueSoon++;
      }
      return acc;
    },
    { complete: 0, active: 0, stale: 0, dueSoon: 0, other: 0 }
  );

  const stats = [
    { label: 'Active', value: counts.active, cls: 'text-primary' },
    { label: 'Complete', value: counts.complete, cls: 'text-emerald-600 dark:text-emerald-400' },
    { label: 'Stale', value: counts.stale, cls: counts.stale > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400' },
    { label: 'Due Soon', value: counts.dueSoon, cls: counts.dueSoon > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400' },
  ];

  return (
    <Card title="Ticket Breakdown" icon={CheckCircle2}>
      <div className="grid grid-cols-2 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl p-5 bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 text-center">
            <div className={`text-2xl font-bold tabular-nums ${s.cls}`}>{s.value}</div>
            <div className="text-meta text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Panel: Delivery Confidence (hero, for exec/sales) ─────────────────────────

function DeliveryHero({ risk, ticketStats }: { risk?: RiskCache; ticketStats?: TicketStats }) {
  const dc = confidenceFromScore(risk?.overallScore);
  // Confidence direction = the risk direction flipped (see ProjectPulse).
  const confidenceDirection = invertDirection(riskTrendToDirection(risk?.trend));
  const pct = ticketStats && ticketStats.total > 0
    ? Math.round((ticketStats.complete / ticketStats.total) * 100)
    : 0;

  return (
    <Card title="Delivery Confidence" className="lg:col-span-2">
      <div className="flex items-center gap-8">
        <div className="text-center">
          <div className={`text-6xl font-black tabular-nums ${dc !== null ? confidenceColor(dc) : 'text-gray-400 dark:text-gray-500'}`}>
            {dc !== null ? `${dc}%` : '—'}
          </div>
          <div className="text-meta text-gray-500 mt-2">Delivery Confidence</div>
          <TrendIndicator direction={confidenceDirection} polarity="higher-is-better" className="flex mt-2" />
        </div>

        <div className="flex-1 space-y-4">
          <div>
            <div className="flex justify-between text-meta text-gray-500 mb-1.5">
              <span>Overall Progress</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
          {ticketStats && (
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="rounded-lg p-2 bg-gray-50 dark:bg-white/[0.03]">
                <div className="text-title font-bold text-emerald-600 dark:text-emerald-400">{ticketStats.complete}</div>
                <div className="text-meta text-gray-400">Complete</div>
              </div>
              <div className="rounded-lg p-2 bg-gray-50 dark:bg-white/[0.03]">
                <div className="text-title font-bold text-primary">{ticketStats.inProgress + ticketStats.todo}</div>
                <div className="text-meta text-gray-400">Remaining</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── Panel: Blockers / Triage ──────────────────────────────────────────────────

function Blockers({ tickets, onTicketSelect }: { tickets: Ticket[]; onTicketSelect: (t: Ticket) => void }) {
  const blockers = tickets.filter((t) => t.status === 'triage' || t.status === 'at_risk');

  return (
    <Card title="Needs Attention" icon={AlertTriangle}>
      {blockers.length === 0 ? (
        <div className="text-center py-4">
          <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
          <p className="text-meta text-gray-500">No blockers or at-risk items</p>
        </div>
      ) : (
        <div className="space-y-4">
          {blockers.slice(0, 5).map((t) => (
            <button
              key={t.ticketId}
              type="button"
              onClick={() => onTicketSelect(t)}
              className="w-full text-left p-2.5 rounded-lg cursor-pointer bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 hover:bg-red-100/70 dark:hover:bg-red-900/20 focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors"
            >
              <p className="text-title text-gray-800 dark:text-gray-200 line-clamp-1">{t.title}</p>
              <p className="text-meta text-gray-500 mt-0.5">
                {t.assigneeName ?? 'Unassigned'} · {t.status === 'triage' ? 'Triage' : 'At Risk'}
              </p>
            </button>
          ))}
          {blockers.length > 5 && (
            <p className="text-meta text-gray-400 text-center pt-1">+{blockers.length - 5} more</p>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Layout by role ────────────────────────────────────────────────────────────

function PMLayout({ tickets, risk, ticketStats, userId, alertThreshold, onTicketSelect }: { tickets: Ticket[]; risk?: RiskCache; ticketStats?: TicketStats; userId: string; alertThreshold: number; onTicketSelect: (t: Ticket) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <ProjectPulse risk={risk} ticketStats={ticketStats} alertThreshold={alertThreshold} />
      <TicketBreakdown tickets={tickets} />
      <MyWork tickets={tickets} userId={userId} onTicketSelect={onTicketSelect} />
      <WhatsNext tickets={tickets} onTicketSelect={onTicketSelect} />
      <TeamWorkload tickets={tickets} />
    </div>
  );
}

function EngineerLayout({ tickets, userId, onTicketSelect }: { tickets: Ticket[]; userId: string; onTicketSelect: (t: Ticket) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <MyWork tickets={tickets} userId={userId} onTicketSelect={onTicketSelect} />
      <Blockers tickets={tickets} onTicketSelect={onTicketSelect} />
      <WhatsNext tickets={tickets} onTicketSelect={onTicketSelect} />
      <TicketBreakdown tickets={tickets} />
    </div>
  );
}

function ExecutiveLayout({ risk, ticketStats, tickets, alertThreshold, onTicketSelect }: { risk?: RiskCache; ticketStats?: TicketStats; tickets: Ticket[]; alertThreshold: number; onTicketSelect: (t: Ticket) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <DeliveryHero risk={risk} ticketStats={ticketStats} />
      <TicketBreakdown tickets={tickets} />
      <ProjectPulse risk={risk} ticketStats={ticketStats} alertThreshold={alertThreshold} />
      <Blockers tickets={tickets} onTicketSelect={onTicketSelect} />
    </div>
  );
}

function TeamLeadLayout({ tickets, risk, ticketStats, userId, alertThreshold, onTicketSelect }: { tickets: Ticket[]; risk?: RiskCache; ticketStats?: TicketStats; userId: string; alertThreshold: number; onTicketSelect: (t: Ticket) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <TeamWorkload tickets={tickets} />
      <Blockers tickets={tickets} onTicketSelect={onTicketSelect} />
      <MyWork tickets={tickets} userId={userId} onTicketSelect={onTicketSelect} />
      <ProjectPulse risk={risk} ticketStats={ticketStats} alertThreshold={alertThreshold} />
      <WhatsNext tickets={tickets} onTicketSelect={onTicketSelect} />
    </div>
  );
}

function DesignerLayout({ tickets, userId, onTicketSelect }: { tickets: Ticket[]; userId: string; onTicketSelect: (t: Ticket) => void }) {
  const designTickets = tickets.filter(
    (t) => (t.title ?? '').toLowerCase().includes('design') || (t.title ?? '').toLowerCase().includes('ui')
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <MyWork tickets={designTickets.length > 0 ? designTickets : tickets} userId={userId} onTicketSelect={onTicketSelect} />
      <Blockers tickets={tickets} onTicketSelect={onTicketSelect} />
      <TicketBreakdown tickets={tickets} />
      <WhatsNext tickets={tickets} onTicketSelect={onTicketSelect} />
    </div>
  );
}

function SalesMarketingLayout({ risk, ticketStats, tickets, alertThreshold }: { risk?: RiskCache; ticketStats?: TicketStats; tickets: Ticket[]; alertThreshold: number }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <DeliveryHero risk={risk} ticketStats={ticketStats} />
      <TicketBreakdown tickets={tickets} />
      <ProjectPulse risk={risk} ticketStats={ticketStats} alertThreshold={alertThreshold} />
    </div>
  );
}

function DefaultLayout({ tickets, risk, ticketStats, userId, alertThreshold, onTicketSelect }: { tickets: Ticket[]; risk?: RiskCache; ticketStats?: TicketStats; userId: string; alertThreshold: number; onTicketSelect: (t: Ticket) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ProjectPulse risk={risk} ticketStats={ticketStats} alertThreshold={alertThreshold} />
      <MyWork tickets={tickets} userId={userId} onTicketSelect={onTicketSelect} />
      <TicketBreakdown tickets={tickets} />
    </div>
  );
}

// ── Action card (single clickable tile) ──────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  product_manager: 'PM', engineer: 'Engineer', executive: 'Executive',
  team_lead: 'Team Lead', designer: 'Designer', sales: 'Sales', marketing: 'Marketing',
};

function ActionCard({ action, projectId, completions }: { action: SuggestedAction; projectId: string; completions?: Record<string, ActionCompletion> }) {
  const [open, setOpen] = useState(false);
  const { getResult, markDone } = useActionStore();
  const completedResult = getResult(action.actionId);
  const completion = completions?.[action.actionId];
  const triggerState = deriveActionTriggerState(completion);
  const isDeemphasized = triggerState === 'done-this-batch';
  const Icon = ACTION_ICONS[action.type] ?? ExternalLink;

  const iconCls =
    triggerState === 'done-this-batch' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500'
    : triggerState === 'needs-your-action' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-500'
    : triggerState === 'failed' ? 'bg-red-50 dark:bg-red-900/20 text-red-500'
    : action.priority === 'high'   ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
    : action.priority === 'medium' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
    :                                'bg-primary/10 dark:bg-primary/20 text-primary';

  const borderHover =
    triggerState === 'needs-your-action' ? 'hover:border-blue-300 dark:hover:border-blue-700/60' :
    triggerState === 'failed'            ? 'hover:border-red-300 dark:hover:border-red-700/60' :
    action.priority === 'high'   ? 'hover:border-red-300 dark:hover:border-red-700/60' :
    action.priority === 'medium' ? 'hover:border-amber-300 dark:hover:border-amber-600/60' :
                                   'hover:border-primary/40 dark:hover:border-primary/30';

  const cardTone =
    triggerState === 'needs-your-action' ? 'border-blue-200 dark:border-blue-900/30 bg-blue-50/40 dark:bg-blue-900/10'
    : triggerState === 'failed' ? 'border-red-200 dark:border-red-900/30 bg-red-50/40 dark:bg-red-900/10'
    : 'border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:bg-gray-50 dark:hover:bg-white/[0.05]';

  const CardIcon =
    triggerState === 'done-this-batch' ? Check
    : triggerState === 'needs-your-action' ? AlertCircle
    : triggerState === 'failed' ? AlertTriangle
    : Icon;

  const subtitle = completion && triggerState !== 'suggested'
    ? completionByLine(completion)
    : action.description;

  return (
    <>
      <motion.button
        whileHover={isDeemphasized ? {} : { scale: 1.015 }}
        whileTap={isDeemphasized ? {} : { scale: 0.985 }}
        onClick={() => setOpen(true)}
        className={`text-left p-3 rounded-xl border transition-all group focus-visible:ring-2 focus-visible:ring-primary/50 ${
          isDeemphasized
            ? 'border-gray-200 dark:border-white/[0.05] bg-gray-50 dark:bg-white/[0.02] opacity-60'
            : `${cardTone} ${borderHover}`
        }`}
      >
        <div className="flex items-start gap-2.5">
          <div className={`flex-shrink-0 p-1.5 rounded-lg ${iconCls} mt-0.5`}>
            <CardIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-meta font-semibold leading-snug line-clamp-1 ${isDeemphasized ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white group-hover:text-primary transition-colors'}`}>
              {action.label}
            </p>
            <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-snug">
              {subtitle}
            </p>
          </div>
        </div>
      </motion.button>

      {open && (
        <ActionModal
          action={action}
          projectId={projectId}
          completedResult={completedResult}
          completion={completion}
          onCompleted={(r) => { markDone(action.actionId, r); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ── Panel: Quick Actions bar (AI-generated only) ──────────────────────────────

async function triggerRefreshActions(projectId: string): Promise<void> {
  const { auth } = await import('../../firebase');
  const token = await auth.currentUser?.getIdToken();
  const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';
  const res = await fetch(`${BASE_URL}/projects/${projectId}/refresh-actions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`refresh-actions failed: ${res.status}`);
}

function ProjectActionsBar({
  risk,
  projectId,
  role,
  functionalRoles,
}: {
  risk?: RiskCache;
  projectId: string;
  role: string;
  functionalRoles: string[];
}) {
  const roleLower = role.toLowerCase();
  const actions = (risk?.projectActions ?? []).filter(
    (a) => roleLower === 'admin' ||
           !a.roles?.length ||
           a.roles.some((r) => r.toLowerCase() === roleLower) ||
           a.roles.some((r) => r.toLowerCase() === 'admin') ||
           functionalRoles.some((fr) => a.roles?.some((r) => r.toLowerCase() === fr.toLowerCase())),
  );

  const hasActions = actions.length > 0;
  // Show generating state if risk was evaluated but no actions written yet
  const pendingGeneration = !risk?.actionsGeneratedAt && !!risk?.generatedAt;

  const qc = useQueryClient();
  const [queued, setQueued] = useState(false);
  const queueTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshMutation = useMutation({
    mutationFn: () => triggerRefreshActions(projectId),
    onSuccess: () => {
      track('project.actions_refresh_queued', 'action', { projectId });
      setQueued(true);
      if (queueTimerRef.current) clearTimeout(queueTimerRef.current);
      // After 45s the worker should be done — clear indicator and refetch risk
      queueTimerRef.current = setTimeout(() => {
        setQueued(false);
        void qc.invalidateQueries({ queryKey: queryKeys.risk(projectId) });
      }, 45_000);
    },
  });

  // Cleanup timer on unmount
  useEffect(() => () => { if (queueTimerRef.current) clearTimeout(queueTimerRef.current); }, []);

  const showSpinner = refreshMutation.isPending || pendingGeneration || (queued && !hasActions);

  return (
    <div className="bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <h3 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Quick Actions
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {ROLE_LABELS[role] && (
            <span className="text-micro font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-white/5 px-2 py-0.5 rounded-full">
              {ROLE_LABELS[role]} view
            </span>
          )}
          <button
            onClick={() => {
              track('project.risk_refresh_clicked', 'action', { projectId });
              refreshMutation.mutate();
            }}
            disabled={refreshMutation.isPending || queued}
            title="Regenerate actions"
            className="p-1 rounded text-gray-400 hover:text-primary transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {hasActions ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {actions.slice(0, 8).map((a) => (
            <ActionCard key={a.actionId} action={a} projectId={projectId} completions={risk?.completions} />
          ))}
        </div>
      ) : showSpinner ? (
        <div className="flex items-center gap-2 py-3 text-meta text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          {queued ? 'Queued — actions will appear in about a minute…' : 'Generating actions based on project context…'}
        </div>
      ) : (
        <p className="text-meta text-gray-400 py-2">
          No actions yet — click refresh to generate based on current project context.
        </p>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function AdaptiveView({ project, tickets, risk, ticketStats, sessionConfig, onTicketSelect }: AdaptiveViewProps) {
  const { currentUser } = useUserStore();

  // Merge JWT claims with Firestore profile so admin-assigned roles are
  // visible immediately without waiting for a token refresh. (Only used for
  // the functionalRoles list passed to ProjectActionsBar below — the role
  // resolution itself goes through useEffectiveRole(), the single source of
  // truth shared with RoleDashboard/AppShell/PMDashboard.)
  const jwtRoles: FunctionalRole[] = sessionConfig?.functionalRoles ?? [];
  const firestoreRoles: FunctionalRole[] = currentUser?.functionalRoles ?? [];
  const functionalRoles: FunctionalRole[] = [
    ...new Set([...jwtRoles, ...firestoreRoles]),
  ] as FunctionalRole[];

  const role = useEffectiveRole();

  const userId = sessionConfig?.userId ?? '';
  const alertThreshold = sessionConfig?.alertThreshold ?? DEFAULT_ALERT_THRESHOLD;

  const layout = useMemo(() => {
    switch (role) {
      case 'product_manager': return <PMLayout tickets={tickets} risk={risk} ticketStats={ticketStats} userId={userId} alertThreshold={alertThreshold} onTicketSelect={onTicketSelect} />;
      case 'engineer': return <EngineerLayout tickets={tickets} userId={userId} onTicketSelect={onTicketSelect} />;
      case 'executive': return <ExecutiveLayout risk={risk} ticketStats={ticketStats} tickets={tickets} alertThreshold={alertThreshold} onTicketSelect={onTicketSelect} />;
      case 'team_lead': return <TeamLeadLayout tickets={tickets} risk={risk} ticketStats={ticketStats} userId={userId} alertThreshold={alertThreshold} onTicketSelect={onTicketSelect} />;
      case 'designer': return <DesignerLayout tickets={tickets} userId={userId} onTicketSelect={onTicketSelect} />;
      case 'sales':
      case 'marketing': return <SalesMarketingLayout risk={risk} ticketStats={ticketStats} tickets={tickets} alertThreshold={alertThreshold} />;
      default: return <DefaultLayout tickets={tickets} risk={risk} ticketStats={ticketStats} userId={userId} alertThreshold={alertThreshold} onTicketSelect={onTicketSelect} />;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, tickets, risk, ticketStats, userId, alertThreshold, onTicketSelect]);

  return (
    <div className="px-6 sm:px-10 lg:px-16 pb-6 pt-4 space-y-4">
      <ProjectActionsBar risk={risk} projectId={project.projectId} role={role} functionalRoles={functionalRoles} />
      {layout}
    </div>
  );
}
